/**
 * Uniform grid for nearest-neighbour queries over splat centres. Used by the LCC2
 * export to find, for every splat of another LOD, its anchor in the loaded LOD.
 * splat-transform has a KdTree, but does not export it.
 */
class AnchorGrid {
    readonly count: number;

    private readonly positions: Float32Array;
    private readonly min: [number, number, number] = [0, 0, 0];
    private readonly dims: [number, number, number] = [1, 1, 1];
    private readonly cellSize: number = 1;
    private readonly cellStart: Uint32Array;
    private readonly items: Uint32Array;

    constructor(positions: Float32Array, pointsPerCell = 4) {
        this.positions = positions;
        const n = this.count = Math.floor(positions.length / 3);
        if (n === 0) {
            this.cellStart = new Uint32Array(2);
            this.items = new Uint32Array(0);
            return;
        }

        const max = [-Infinity, -Infinity, -Infinity];
        const min = [Infinity, Infinity, Infinity];
        for (let i = 0; i < n; ++i) {
            for (let a = 0; a < 3; ++a) {
                const v = positions[i * 3 + a];
                if (v < min[a]) min[a] = v;
                if (v > max[a]) max[a] = v;
            }
        }
        const extent = max.map((v, a) => Math.max(v - min[a], 1e-6));
        const targetCells = Math.max(1, Math.min(n / pointsPerCell, 1 << 26));

        // start from the volume estimate; flat or thin clouds make it too small,
        // so grow the cells until the cell count is bounded
        let cellSize = Math.max(Math.cbrt(extent[0] * extent[1] * extent[2] / targetCells), 1e-6);
        const dimsFor = (size: number) => extent.map(e => Math.min(Math.floor(e / size) + 1, 1 << 20));
        while (dimsFor(cellSize).reduce((p, d) => p * d, 1) > targetCells * 8) {
            cellSize *= 1.5;
        }
        const dims = dimsFor(cellSize);

        this.min = [min[0], min[1], min[2]];
        this.dims = [dims[0], dims[1], dims[2]];
        this.cellSize = cellSize;

        const numCells = dims[0] * dims[1] * dims[2];
        const cellOf = new Uint32Array(n);
        const cellStart = new Uint32Array(numCells + 1);
        for (let i = 0; i < n; ++i) {
            const c = this.cellIndex(
                this.clampCell(positions[i * 3], 0),
                this.clampCell(positions[i * 3 + 1], 1),
                this.clampCell(positions[i * 3 + 2], 2)
            );
            cellOf[i] = c;
            cellStart[c + 1]++;
        }
        for (let c = 0; c < numCells; ++c) cellStart[c + 1] += cellStart[c];
        const cursor = cellStart.slice(0, numCells);
        const items = new Uint32Array(n);
        for (let i = 0; i < n; ++i) items[cursor[cellOf[i]]++] = i;

        this.cellStart = cellStart;
        this.items = items;
    }

    private clampCell(v: number, axis: number) {
        const c = Math.floor((v - this.min[axis]) / this.cellSize);
        return c < 0 ? 0 : (c >= this.dims[axis] ? this.dims[axis] - 1 : c);
    }

    private cellIndex(i: number, j: number, k: number) {
        return i + this.dims[0] * (j + this.dims[1] * k);
    }

    nearest(x: number, y: number, z: number): number {
        if (this.count === 0) return -1;

        const { dims, min, cellSize, positions, items, cellStart } = this;
        const c = [this.clampCell(x, 0), this.clampCell(y, 1), this.clampCell(z, 2)];
        const q = [x, y, z];
        let best = -1;
        let bestD = Infinity;

        const scan = (i: number, j: number, k: number) => {
            if (i < 0 || j < 0 || k < 0 || i >= dims[0] || j >= dims[1] || k >= dims[2]) return;
            const cell = this.cellIndex(i, j, k);
            for (let s = cellStart[cell]; s < cellStart[cell + 1]; ++s) {
                const row = items[s];
                const dx = positions[row * 3] - x;
                const dy = positions[row * 3 + 1] - y;
                const dz = positions[row * 3 + 2] - z;
                const d = dx * dx + dy * dy + dz * dz;
                if (d < bestD || (d === bestD && row < best)) {
                    bestD = d;
                    best = row;
                }
            }
        };

        const maxR = Math.max(dims[0], dims[1], dims[2]);
        for (let r = 0; r <= maxR; ++r) {
            // visit only the shell at Chebyshev distance r
            for (let i = c[0] - r; i <= c[0] + r; ++i) {
                const iEdge = Math.abs(i - c[0]) === r;
                for (let j = c[1] - r; j <= c[1] + r; ++j) {
                    if (iEdge || Math.abs(j - c[1]) === r) {
                        for (let k = c[2] - r; k <= c[2] + r; ++k) scan(i, j, k);
                    } else {
                        scan(i, j, c[2] - r);
                        if (r > 0) scan(i, j, c[2] + r);
                    }
                }
            }

            // anything not searched yet lies beyond the searched box
            let bound = Infinity;
            let covered = true;
            for (let a = 0; a < 3; ++a) {
                if (c[a] - r > 0) {
                    covered = false;
                    bound = Math.min(bound, q[a] - (min[a] + (c[a] - r) * cellSize));
                }
                if (c[a] + r < dims[a] - 1) {
                    covered = false;
                    bound = Math.min(bound, min[a] + (c[a] + r + 1) * cellSize - q[a]);
                }
            }
            if (covered) break;
            bound = Math.max(0, bound);
            // strict: an equally distant, smaller row may still lie outside
            if (best >= 0 && bestD < bound * bound) break;
        }

        return best;
    }
}

export { AnchorGrid };
